import React, { useState } from "react";
import {
  Check,
  FileText,
  CheckCircle2,
  Circle,
  ClipboardList,
  Sparkles,
  ExternalLink,
  BookOpen,
  Eye,
  PenLine,
  UserX,
} from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { queryClient } from "@/ajax/queryClient";
import { useTRPC, useTRPCClient } from "@/trpc";
import {
  HiringProcessAssigneeRole,
  HiringProcessItemStatus,
  HiringProcessItemType,
} from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";
import { FormSubmissionDialog } from "@/features/hiring-process/form-submission-dialog";
import { FormResponseDialog } from "@/features/hiring-process/form-response-dialog";
import { JobPostDetailDialog } from "@/features/job-post/components/job-post-detail-dialog";
import { JobPostWithdrawDialog } from "@/features/job-post/job-post-withdraw-dialog";
import { ChatAttachmentPreviewModal } from "@/features/chat/ChatAttachmentPreviewModal";
import { ProviderDocumentUpload } from "@/features/hiring-process/provider-document-upload";
import { PdfSignDialog } from "./pdf/pdf-sign-dialog";
import { SharedResourceList } from "./shared-resource-list";

type HiringProcessInstance = NonNullable<
  RouterOutputs["hiringProcess"]["getMyProcess"]
>;
type ProviderItem = HiringProcessInstance["items"][number];

interface ProviderItemRowProps {
  item: ProviderItem;
  jobPostId: string;
  onStatusChange: (itemId: string, status: HiringProcessItemStatus) => void;
  isUpdating: boolean;
  onAttachmentsChanged: () => void;
  candidateUserId: string;
}

function ProviderItemRow({
  item,
  jobPostId,
  onStatusChange,
  isUpdating,
  onAttachmentsChanged,
  candidateUserId,
}: ProviderItemRowProps) {
  const trpcClient = useTRPCClient();
  const [formSubmitOpen, setFormSubmitOpen] = useState(false);
  const [formResponseOpen, setFormResponseOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);

  const isFormItem = item.itemType === HiringProcessItemType.Form;
  const isTaskItem = item.itemType === HiringProcessItemType.Task;
  const isDocShare = item.itemType === HiringProcessItemType.DocumentShare;
  const isSigningItem = item.itemType === HiringProcessItemType.DocumentSigning;
  const isCompleted = item.status === HiringProcessItemStatus.Completed;
  const sharedFiles = [
    ...(isDocShare ? (item.fileAttachments ?? []) : []),
    ...(item.sharedFromPredecessors?.files ?? []),
  ].sort((a, b) => a.sortOrder - b.sortOrder);
  const sharedLinks = [
    ...(isDocShare ? (item.links ?? []) : []),
    ...(item.sharedFromPredecessors?.links ?? []),
  ].sort((a, b) => a.sortOrder - b.sortOrder);
  const primarySharedFile = sharedFiles.find(
    (attachment) => attachment.fileUpload.scanStatus === "clean",
  );
  const primarySharedFilePreviewable =
    !!primarySharedFile &&
    (primarySharedFile.fileUpload.mimeType === "application/pdf" ||
      primarySharedFile.fileUpload.mimeType.startsWith("image/"));
  const [confirmedRead, setConfirmedRead] = useState(false);
  const [inFlightId, setInFlightId] = useState<string | null>(null);
  const [inFlightAction, setInFlightAction] = useState<
    "download" | "preview" | null
  >(null);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    fileName: string;
    mimeType: string;
  } | null>(null);

  const handleFileDownload = async (
    fileUploadId: string,
    hiringProcessItemId: string,
  ) => {
    if (inFlightId) {
      return;
    }
    setInFlightId(fileUploadId);
    setInFlightAction("download");
    try {
      const result =
        await trpcClient.hiringProcess.getItemFileDownloadUrl.query({
          fileUploadId,
          jobPostId,
          hiringProcessItemId,
        });
      window.open(result.signedUrl, "_blank");
    } catch {
      // silently fail — URL fetch errors are non-critical
    } finally {
      setInFlightId(null);
      setInFlightAction(null);
    }
  };

  const handleFilePreview = async (
    fileUploadId: string,
    hiringProcessItemId: string,
    fileName: string,
    mimeType: string,
  ) => {
    if (inFlightId) {
      return;
    }
    setInFlightId(fileUploadId);
    setInFlightAction("preview");
    try {
      const result =
        await trpcClient.hiringProcess.getItemFileDownloadUrl.query({
          fileUploadId,
          jobPostId,
          hiringProcessItemId,
        });
      setPreviewFile({ url: result.signedUrl, fileName, mimeType });
    } catch {
      // silently fail — URL fetch errors are non-critical
    } finally {
      setInFlightId(null);
      setInFlightAction(null);
    }
  };

  return (
    <div
      className={`group relative rounded-xl border p-4 transition-all duration-200 ${
        isCompleted
          ? "border-green-100 bg-green-50/50"
          : "border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm"
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Status icon */}
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
            isCompleted
              ? "bg-green-100"
              : isFormItem
                ? "bg-purple-50"
                : "bg-blue-50"
          }`}
        >
          {isCompleted ? (
            <CheckCircle2 size={16} className="text-green-600" />
          ) : isFormItem ? (
            <FileText size={15} className="text-purple-600" />
          ) : isDocShare ? (
            <BookOpen size={15} className="text-blue-400" />
          ) : isSigningItem ? (
            <PenLine size={15} className="text-green-600" />
          ) : (
            <Circle size={15} className="text-blue-400" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between gap-3 flex-wrap items-center">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-sm font-semibold leading-snug ${
                    isCompleted ? "text-gray-400 line-through" : "text-gray-900"
                  }`}
                >
                  {item.title}
                </span>
                {!item.isRequired && (
                  <span className="text-xs text-gray-400 font-normal bg-gray-100 rounded px-1.5 py-0.5">
                    optional
                  </span>
                )}
                {isFormItem && !isCompleted && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-purple-50 text-purple-700 border-purple-200 h-5 px-1.5"
                  >
                    <FileText size={9} className="mr-1" />
                    Form
                  </Badge>
                )}
                {isDocShare && !isCompleted && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-blue-50 text-blue-700 border-blue-200 h-5 px-1.5"
                  >
                    <BookOpen size={9} className="mr-1" />
                    Document
                  </Badge>
                )}
                {isSigningItem && !isCompleted && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-green-50 text-green-700 border-green-200 h-5 px-1.5"
                  >
                    <PenLine size={9} className="mr-1" />
                    PDF
                  </Badge>
                )}
              </div>
              {item.description && (
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  {item.description}
                </p>
              )}
            </div>

            <div className="shrink-0">
              {isCompleted ? (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium bg-green-100 rounded-full px-2.5 py-1">
                    <Check size={11} />
                    Done
                  </span>
                  {(isFormItem || isSigningItem) && item.formSubmissionId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                      onClick={() => setFormResponseOpen(true)}
                    >
                      <Eye size={12} className="mr-1" />
                      View
                    </Button>
                  )}
                  {isDocShare && primarySharedFile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                      onClick={() =>
                        primarySharedFilePreviewable
                          ? handleFilePreview(
                              primarySharedFile.fileUpload.id,
                              item.hiringProcessItemId,
                              primarySharedFile.displayName,
                              primarySharedFile.fileUpload.mimeType,
                            )
                          : handleFileDownload(
                              primarySharedFile.fileUpload.id,
                              item.hiringProcessItemId,
                            )
                      }
                      disabled={!!inFlightId}
                    >
                      <Eye size={12} className="mr-1" />
                      {primarySharedFilePreviewable ? "View" : "Download"}
                    </Button>
                  )}
                  {isDocShare && !primarySharedFile && sharedLinks[0] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                      onClick={() =>
                        window.open(sharedLinks[0].url, "_blank", "noreferrer")
                      }
                    >
                      <ExternalLink size={12} className="mr-1" />
                      Open
                    </Button>
                  )}
                </div>
              ) : isFormItem && item.formId ? (
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 shadow-sm"
                  onClick={() => setFormSubmitOpen(true)}
                  disabled={isUpdating}
                >
                  <FileText size={12} className="mr-1.5" />
                  Fill Out Form
                </Button>
              ) : isSigningItem && item.fileFormId ? (
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 shadow-sm"
                  onClick={() => setSignOpen(true)}
                  disabled={isUpdating}
                >
                  <PenLine size={12} className="mr-1.5" />
                  Complete PDF
                </Button>
              ) : isTaskItem ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-3 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() =>
                    onStatusChange(item.id, HiringProcessItemStatus.Completed)
                  }
                  disabled={isUpdating}
                >
                  <Check size={12} className="mr-1.5" />
                  Mark Complete
                </Button>
              ) : isDocShare && primarySharedFile ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-3 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() =>
                    primarySharedFilePreviewable
                      ? handleFilePreview(
                          primarySharedFile.fileUpload.id,
                          item.hiringProcessItemId,
                          primarySharedFile.displayName,
                          primarySharedFile.fileUpload.mimeType,
                        )
                      : handleFileDownload(
                          primarySharedFile.fileUpload.id,
                          item.hiringProcessItemId,
                        )
                  }
                  disabled={isUpdating || !!inFlightId}
                >
                  <Eye size={12} className="mr-1.5" />
                  {primarySharedFilePreviewable
                    ? "View Document"
                    : "Download Document"}
                </Button>
              ) : isDocShare && sharedLinks[0] ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-3 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() =>
                    window.open(sharedLinks[0].url, "_blank", "noreferrer")
                  }
                  disabled={isUpdating}
                >
                  <ExternalLink size={12} className="mr-1.5" />
                  Open Link
                </Button>
              ) : isDocShare &&
                !item.richTextContent &&
                sharedFiles.length === 0 &&
                sharedLinks.length === 0 ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-3 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() =>
                    onStatusChange(item.id, HiringProcessItemStatus.Completed)
                  }
                  disabled={isUpdating}
                >
                  <Check size={12} className="mr-1.5" />
                  Mark as Reviewed
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {isDocShare && item.richTextContent && (
        <div className="ml-11 mt-2">
          <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-700 whitespace-pre-wrap border border-gray-100">
            {item.richTextContent}
          </div>
        </div>
      )}

      {/*
       * Files + links on this item:
       *   - DocShare items: the item's own template attachments/links.
       *   - Any item type: content passed through from predecessors with
       *     shareContent=true. Backend already gates — no client filter.
       */}
      {(() => {
        const allFiles = sharedFiles;
        const allLinks = sharedLinks;
        if (allFiles.length === 0 && allLinks.length === 0) {
          return null;
        }
        return (
          <SharedResourceList
            className="ml-11 mt-3"
            files={allFiles}
            links={allLinks}
            inFlightId={inFlightId}
            inFlightAction={inFlightAction}
            onPreviewFile={(attachment) =>
              handleFilePreview(
                attachment.fileUpload.id,
                item.hiringProcessItemId,
                attachment.displayName,
                attachment.fileUpload.mimeType,
              )
            }
            onDownloadFile={(attachment) =>
              handleFileDownload(
                attachment.fileUpload.id,
                item.hiringProcessItemId,
              )
            }
          />
        );
      })()}

      {isDocShare &&
        !isCompleted &&
        (item.richTextContent ||
          sharedFiles.length > 0 ||
          sharedLinks.length > 0) && (
          <div className="ml-11 mt-3 flex items-center gap-2">
            <Checkbox
              id={`confirm-read-dialog-${item.id}`}
              checked={confirmedRead}
              disabled={isUpdating || confirmedRead}
              onCheckedChange={(checked) => {
                if (checked === true) {
                  setConfirmedRead(true);
                  onStatusChange(item.id, HiringProcessItemStatus.Completed);
                }
              }}
            />
            <Label
              htmlFor={`confirm-read-dialog-${item.id}`}
              className="text-xs text-gray-600 cursor-pointer"
            >
              I confirm I have read this document
            </Label>
          </div>
        )}

      {/* Provider document upload panel */}
      {item.itemType === HiringProcessItemType.DocumentUpload &&
        item.assigneeRole === HiringProcessAssigneeRole.Provider && (
          <ProviderDocumentUpload
            hiringProcessItemProgressId={item.hiringProcessItemProgressId}
            hiringProcessItemId={item.hiringProcessItemId}
            jobPostId={jobPostId}
            requestedCredentialFileTypeId={item.requestedCredentialFileTypeId}
            existingAttachments={item.providerAttachments ?? []}
            onAttachmentsChanged={onAttachmentsChanged}
            status={item.status}
            onMarkComplete={() =>
              onStatusChange(
                item.hiringProcessItemId,
                HiringProcessItemStatus.Completed,
              )
            }
            onReopen={() =>
              onStatusChange(
                item.hiringProcessItemId,
                HiringProcessItemStatus.InProgress,
              )
            }
            isUpdatingStatus={isUpdating}
          />
        )}

      {isFormItem && item.formId && (
        <FormSubmissionDialog
          open={formSubmitOpen}
          onOpenChange={setFormSubmitOpen}
          formId={item.formId}
          anchor={{
            kind: "hiring",
            jobPostId,
            hiringProcessItemId: item.hiringProcessItemId,
          }}
          suggestionsEnabled
        />
      )}

      {(isFormItem || isSigningItem) && item.formSubmissionId && (
        <FormResponseDialog
          open={formResponseOpen}
          onOpenChange={setFormResponseOpen}
          formSubmissionId={item.formSubmissionId}
        />
      )}

      {isSigningItem && item.fileFormId && (
        <PdfSignDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          formId={item.fileFormId}
          anchor={{
            kind: "hiring",
            jobPostId,
            hiringProcessItemId: item.hiringProcessItemId,
            candidateUserId,
          }}
          title={item.title}
          onSigned={onAttachmentsChanged}
        />
      )}

      {previewFile && (
        <ChatAttachmentPreviewModal
          url={previewFile.url}
          fileName={previewFile.fileName}
          mimeType={previewFile.mimeType}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
}

interface HiringProcessChecklistDialogProps {
  jobPostId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HiringProcessChecklistDialog({
  jobPostId,
  open,
  onOpenChange,
}: HiringProcessChecklistDialogProps) {
  const trpc = useTRPC();
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);
  const [jobDetailOpen, setJobDetailOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const { data: instance } = useQuery({
    ...trpc.hiringProcess.getMyProcess.queryOptions({ jobPostId }),
    enabled: open,
  });

  const providerItems =
    instance?.items.filter(
      (i) => i.assigneeRole === HiringProcessAssigneeRole.Provider,
    ) ?? [];

  const pendingItems = providerItems.filter(
    (i) => i.status !== HiringProcessItemStatus.Completed,
  );
  const completedCount = providerItems.length - pendingItems.length;
  const allDone = pendingItems.length === 0;
  const progressPct =
    providerItems.length > 0
      ? Math.round((completedCount / providerItems.length) * 100)
      : 100;

  const updateStatusMutation = useMutation(
    trpc.hiringProcess.updateItemStatus.mutationOptions({
      onSuccess: async () => {
        setUpdatingItemId(null);
        await queryClient.invalidateQueries(
          trpc.hiringProcess.getMyProcess.queryOptions({ jobPostId }),
        );
        await queryClient.invalidateQueries(
          trpc.hiringProcess.getMyProcesses.queryOptions(),
        );
      },
      onError: () => {
        setUpdatingItemId(null);
        toast.error("Failed to update item");
      },
    }),
  );

  const handleStatusChange = (
    hiringProcessItemId: string,
    status: HiringProcessItemStatus,
  ) => {
    if (!instance) {
      return;
    }
    setUpdatingItemId(hiringProcessItemId);
    updateStatusMutation.mutate({
      jobPostId,
      userId: instance.userId,
      hiringProcessItemId,
      status,
    });
  };

  const handleAttachmentsChanged = async () => {
    await Promise.all([
      queryClient.invalidateQueries(
        trpc.hiringProcess.getMyProcess.queryOptions({ jobPostId }),
      ),
      queryClient.invalidateQueries(
        trpc.hiringProcess.getMyProcesses.queryOptions(),
      ),
    ]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:min-w-[500px] sm:max-w-lg p-0 overflow-hidden gap-0">
        {/* Header band */}
        <div
          className={`px-6 pt-6 pb-5 ${allDone ? "bg-gradient-to-br from-green-50 to-emerald-50/60" : "bg-gradient-to-br from-blue-50 to-indigo-50/60"}`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                allDone ? "bg-green-100" : "bg-blue-100"
              }`}
            >
              {allDone ? (
                <Sparkles size={20} className="text-green-600" />
              ) : (
                <ClipboardList size={20} className="text-blue-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-semibold text-gray-900">
                {allDone ? "You're all set!" : "Complete Your Application"}
              </DialogTitle>
              {instance?.jobPost?.title && (
                <button
                  type="button"
                  onClick={() => setJobDetailOpen(true)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline mt-0.5 flex items-center gap-1 text-left"
                >
                  {instance.jobPost.title}
                  <ExternalLink size={12} className="shrink-0" />
                </button>
              )}
              <DialogDescription className="mt-1 text-sm text-gray-500 leading-relaxed">
                {allDone
                  ? "You've completed everything required. The organization will review your application."
                  : "The organization requires the following to complete your application."}
              </DialogDescription>
            </div>
          </div>

          {/* Progress bar */}
          {providerItems.length > 1 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-gray-500">
                  Progress
                </span>
                <span
                  className={`text-xs font-semibold ${allDone ? "text-green-700" : "text-blue-700"}`}
                >
                  {completedCount} / {providerItems.length} complete
                </span>
              </div>
              <Progress
                value={progressPct}
                className={`h-1.5 ${allDone ? "bg-green-100 [&>div]:bg-green-500" : "bg-blue-100 [&>div]:bg-blue-500"}`}
              />
            </div>
          )}
        </div>

        {/* Items section */}
        {!allDone && (
          <div className="px-6 py-4 max-h-[45vh] overflow-y-auto">
            <div className="flex flex-col gap-2">
              {providerItems.map((item) => (
                <ProviderItemRow
                  key={item.id}
                  item={item}
                  jobPostId={jobPostId}
                  onStatusChange={handleStatusChange}
                  isUpdating={
                    updatingItemId === item.id && updateStatusMutation.isPending
                  }
                  onAttachmentsChanged={handleAttachmentsChanged}
                  candidateUserId={instance!.userId}
                />
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div
          className={`px-6 py-4 flex items-center ${!allDone ? "justify-between border-t border-gray-100 bg-gray-50/60" : "justify-end pt-2 pb-6"}`}
        >
          {!allDone && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setWithdrawOpen(true)}
              className="text-xs font-medium text-gray-500 hover:text-red-600 hover:bg-red-50/60 gap-1.5 -ml-2"
            >
              <UserX size={13} className="opacity-70" />
              Withdraw application
            </Button>
          )}

          <Button
            type="button"
            variant={allDone ? "default" : "ghost"}
            onClick={() => onOpenChange(false)}
            className={
              allDone
                ? "bg-green-600 hover:bg-green-700 text-white px-6"
                : "text-gray-500 hover:text-gray-700 text-sm"
            }
          >
            {allDone ? "Done" : "I'll do this later"}
          </Button>
        </div>
      </DialogContent>

      <JobPostDetailDialog
        jobId={jobPostId}
        open={jobDetailOpen}
        onOpenChange={setJobDetailOpen}
      />

      <JobPostWithdrawDialog
        jobId={jobPostId}
        jobTitle={instance?.jobPost?.title ?? undefined}
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        onSuccess={async () => {
          onOpenChange(false);
          await Promise.all([
            queryClient.invalidateQueries(
              trpc.hiringProcess.getMyProcess.queryOptions({ jobPostId }),
            ),
            queryClient.invalidateQueries(
              trpc.hiringProcess.getMyProcesses.queryOptions(),
            ),
            queryClient.invalidateQueries({
              queryKey: trpc.jobPost.get.queryKey({ id: jobPostId }),
            }),
          ]);
        }}
      />
    </Dialog>
  );
}
